import { useState, useEffect } from "react";
import { Eye, CheckCircle2, EyeOff, Database, Globe, Store, Users, Target, Zap } from "lucide-react";

// Hooks
import { useIgnoredOffers } from '../../hooks/useIgnoredOffers';
import { useExpectedData } from '../../hooks/useExpectedData';

// Layout Components
import ModalHeader from './shared/ModalHeader';
import TabNavigation from './shared/TabNavigation';

// Tab Components
import OverviewTab from './tabs/OverviewTab';
import ValidationTab from './tabs/ValidationTab';
import IgnoredOffersTab from './tabs/IgnoredOffersTab';
import ExpectedDataTab from './tabs/ExpectedDataTab';
import PagesTab from './tabs/PagesTab';
import StoreInfoTab from './tabs/StoreInfoTab';

export default function StoreDetailsModal({ store, validationResults, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [expandedPages, setExpandedPages] = useState({});

    // Custom hooks
    const {
        ignoredOffers,
        ignoreOffer,
        restoreOffer,
        isOfferIgnored,
        hasIgnoredOffers
    } = useIgnoredOffers(store.name);

    const {
        expectedData,
        loading: loadingExpectedData
    } = useExpectedData(store.name, validationResults);

    // Modal open class management
    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    const togglePage = (pageUrl) => {
        setExpandedPages(prev => ({ ...prev, [pageUrl]: !prev[pageUrl] }));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const exportResults = () => {
        const exportData = {
            store: store,
            validationResults: validationResults,
            expectedData: expectedData,
            timestamp: new Date().toISOString()
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `store-details-${store.name}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const domainConfig = {
        'PPC': { icon: <Target size={14} />, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
        'Facebook': { icon: <Users size={14} />, color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
        'More': { icon: <Zap size={14} />, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <Eye size={16} /> },
        { id: 'validation', label: 'Validation Results', icon: <CheckCircle2 size={16} /> },
        ...(hasIgnoredOffers ? [{ id: 'ignored', label: 'Ignored Offers', icon: <EyeOff size={16} /> }] : []),
        { id: 'expected', label: 'Expected Data', icon: <Database size={16} /> },
        { id: 'pages', label: 'Pages', icon: <Globe size={16} /> },
        { id: 'store-info', label: 'Store Info', icon: <Store size={16} /> }
    ];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
                className="bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col border border-slate-200/60"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'calc(100vw - 2rem)',
                    maxWidth: '1280px',
                    height: '90vh',
                    minHeight: '90vh'
                }}
            >
                <ModalHeader
                    store={store}
                    validationResults={validationResults}
                    onClose={onClose}
                    onExport={exportResults}
                />

                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                <div className="overflow-y-auto flex-1 min-h-0">
                    {activeTab === 'overview' && (
                        <OverviewTab
                            store={store}
                            validationResults={validationResults}
                            domainConfig={domainConfig}
                        />
                    )}

                    {activeTab === 'validation' && (
                        <ValidationTab
                            store={store}
                            validationResults={validationResults}
                            expandedPages={expandedPages}
                            togglePage={togglePage}
                            ignoredOffers={ignoredOffers}
                            ignoreOffer={ignoreOffer}
                            restoreOffer={restoreOffer}
                            isOfferIgnored={isOfferIgnored}
                            copyToClipboard={copyToClipboard}
                        />
                    )}

                    {activeTab === 'ignored' && (
                        <IgnoredOffersTab
                            ignoredOffers={ignoredOffers}
                            validationResults={validationResults}
                            restoreOffer={restoreOffer}
                            copyToClipboard={copyToClipboard}
                        />
                    )}

                    {activeTab === 'expected' && (
                        <ExpectedDataTab
                            expectedData={expectedData}
                            loading={loadingExpectedData}
                        />
                    )}

                    {activeTab === 'pages' && (
                        <PagesTab
                            store={store}
                            domainConfig={domainConfig}
                        />
                    )}

                    {activeTab === 'store-info' && (
                        <StoreInfoTab
                            store={store}
                            domainConfig={domainConfig}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
